import { useState } from "react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Users,
  Sparkles,
  HeartHandshake,
  BookOpen,
  Lock,
} from "lucide-react";

const PRESETS = {
  USD: [5, 10, 25, 50, 100],
  NGN: [2500, 5000, 10000, 25000, 50000],
};

const SYMBOL = { USD: "$", NGN: "₦" };

export default function Membership() {
  const [currency, setCurrency] = useState("USD");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    pledged_amount: "",
    motivation: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(form.pledged_amount);
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    if (!amount || amount < 1) {
      toast.error("Please enter the amount you'd like to pledge each month");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/membership", { ...form, pledged_amount: amount, currency });
      setSubmitted(true);
      toast.success("Welcome to the Irene community.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const perks = [
    { icon: Users, title: "Community", body: "Join a growing circle of educators, engineers, parents, and advocates who care about African education." },
    { icon: BookOpen, title: "Field updates", body: "Private monthly briefings from the Irene team — photos, numbers, unfiltered field notes." },
    { icon: HeartHandshake, title: "Early access", body: "First invitations to volunteer trips, panels, and in-person builds." },
    { icon: Sparkles, title: "Your name on the wall", body: "Every rebuilt classroom carries the names of its first community members." },
  ];

  return (
    <div data-testid="membership-page" className="min-h-screen bg-[var(--bone)] text-[var(--ink)]">
      <Nav />

      {/* HERO */}
      <section className="pt-32 pb-16 md:pt-40 bg-[var(--forest)] text-[var(--bone)] relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-50" />
        <div aria-hidden className="hidden md:block absolute -right-20 top-10 font-serif text-[32rem] leading-none text-[var(--bone)]/[0.04] select-none">
          M
        </div>
        <div className="relative container-x">
          <div className="overline text-[var(--terracotta)]">Membership</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[7rem] mt-5 tracking-[-0.035em] leading-[0.95] max-w-5xl">
            Join the <em className="text-[var(--terracotta)] not-italic">Irene community.</em>
          </h1>
          <p className="text-[var(--bone)]/85 mt-8 max-w-2xl leading-relaxed text-base md:text-lg">
            Membership is how we build a community of like-minded people who believe
            every child — especially every girl — deserves a classroom that works.
            It is a <strong className="text-[var(--bone)]">monthly fee</strong>; the amount is entirely up to you — pay what you
            can afford each month, in <strong className="text-[var(--bone)]">USD or Naira</strong>.
          </p>

          <div className="mt-8 inline-flex items-start gap-3 bg-[var(--bone)]/5 border border-[var(--bone)]/20 px-5 py-4 max-w-2xl">
            <Lock className="w-4 h-4 text-[var(--terracotta)] mt-0.5 shrink-0" />
            <p className="text-sm text-[var(--bone)]/85 leading-relaxed">
              <strong className="text-[var(--bone)]">Not active yet.</strong> Memberships are not being
              charged until our registration and financial reporting are complete — we care about full transparency
              from day one. Add your name now and we will reach out the moment it goes live.
            </p>
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="py-24 md:py-32">
        <div className="container-x">
          <div className="grid grid-cols-12 gap-6 items-end mb-14">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-[var(--terracotta)]">Why become a member</div>
              <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-[-0.02em] leading-[1.05]">
                It&apos;s not about the money.
                <br/>
                <em className="text-[var(--terracotta)] not-italic">It&apos;s about belonging.</em>
              </h2>
            </div>
            <p className="col-span-12 md:col-span-4 text-sm text-[var(--ink)]/70">
              Members are the first people we call when a classroom is ready, a student
              graduates, or a girl walks into her first science lab.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {perks.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.title} data-testid={`perk-${i}`} className="border border-black/10 p-7 bg-[var(--bone-2)]">
                  <Icon className="w-8 h-8 text-[var(--terracotta)]" strokeWidth={1.5} />
                  <div className="font-serif text-2xl mt-6 tracking-[-0.015em]">{p.title}</div>
                  <p className="text-sm text-[var(--ink)]/70 mt-3 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="pb-24 md:pb-32">
        <div className="container-x grid grid-cols-12 gap-10 items-start">
          <div className="col-span-12 md:col-span-5">
            <div className="overline text-[var(--terracotta)]">Pay what you can</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 tracking-[-0.02em] leading-[1.05]">
              The amount is <em className="text-[var(--terracotta)] not-italic">your choice.</em>
            </h2>
            <p className="text-[var(--ink)]/75 mt-6 leading-relaxed max-w-md">
              Pledge whatever you can afford each month — in US dollars or Naira.
              Student stipend or CEO cheque, everyone is a member on the same footing once you join.
              We&apos;ll follow up with payment details as soon as our processing is live.
            </p>
            <div className="mt-10 border-l-4 border-[var(--terracotta)] pl-5">
              <p className="font-serif italic text-lg leading-snug text-[var(--ink)]/90">
                &ldquo;When you educate a girl, you educate a future.&rdquo;
              </p>
              <div className="overline text-[var(--muted)] mt-3">— Ella Iwere, Founder</div>
            </div>
          </div>

          <div className="col-span-12 md:col-span-7 md:pl-8">
            <div className="bg-[var(--forest)] text-[var(--bone)] p-10 md:p-14 relative overflow-hidden">
              <div className="grain absolute inset-0" />
              <div className="relative">
                {submitted ? (
                  <div data-testid="member-thanks" className="py-6">
                    <CheckCircle2 className="w-10 h-10 text-[var(--terracotta)]" />
                    <h3 className="font-serif text-4xl mt-6 tracking-[-0.02em] leading-[1.05]">
                      Welcome home.
                    </h3>
                    <p className="text-[var(--bone)]/75 mt-4 max-w-md">
                      You are now part of the Irene community. We will reach out with
                      payment details and your first private field update the moment
                      memberships go live.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} data-testid="membership-form">
                    <div className="overline text-[var(--terracotta)]">Become a member</div>
                    <h3 className="font-serif text-3xl md:text-4xl mt-4 tracking-[-0.02em] leading-[1.05]">
                      Tell us who&apos;s joining.
                    </h3>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Full name</span>
                        <input data-testid="member-name" required className="editorial-input on-dark mt-2" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Your full name" />
                      </label>
                      <label className="block">
                        <span className="overline text-[var(--bone)]/60">Email</span>
                        <input data-testid="member-email" required type="email" className="editorial-input on-dark mt-2" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="you@example.com" />
                      </label>
                      <label className="block md:col-span-2">
                        <span className="overline text-[var(--bone)]/60">Phone (optional)</span>
                        <input data-testid="member-phone" className="editorial-input on-dark mt-2" value={form.phone} onChange={(e) => upd("phone", e.target.value)} placeholder="+234 …" />
                      </label>
                    </div>

                    <div className="mt-10">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="overline text-[var(--bone)]/60">Monthly pledge — pay what you can</div>
                        <div className="inline-flex border border-[var(--bone)]/25 p-0.5">
                          {["USD", "NGN"].map((c) => (
                            <button
                              key={c}
                              type="button"
                              data-testid={`currency-${c}`}
                              onClick={() => { setCurrency(c); upd("pledged_amount", ""); }}
                              className={`px-3 py-1 text-xs font-mono tracking-wider transition-colors ${
                                currency === c
                                  ? "bg-[var(--terracotta)] text-[var(--bone)]"
                                  : "text-[var(--bone)]/70 hover:text-[var(--bone)]"
                              }`}
                            >
                              {c === "USD" ? "$ USD" : "₦ NGN"}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-end gap-3 mt-5 flex-wrap">
                        <div className="flex items-end">
                          <span className="font-serif text-5xl text-[var(--terracotta)] pr-1">
                            {SYMBOL[currency]}
                          </span>
                          <input
                            data-testid="member-amount"
                            type="number"
                            min="1"
                            step={currency === "NGN" ? "100" : "1"}
                            required
                            className="bg-transparent border-b border-[var(--bone)]/40 focus:border-[var(--terracotta)] outline-none font-serif text-5xl md:text-6xl w-40 md:w-56 py-1 text-[var(--bone)]"
                            value={form.pledged_amount}
                            onChange={(e) => upd("pledged_amount", e.target.value)}
                            placeholder={currency === "NGN" ? "5000" : "25"}
                          />
                        </div>
                        <span className="pb-3 font-mono text-xs text-[var(--bone)]/60">/ month</span>
                      </div>

                      <div className="flex flex-wrap gap-2 mt-4">
                        {PRESETS[currency].map((n) => (
                          <button
                            key={n}
                            type="button"
                            data-testid={`member-pick-${currency}-${n}`}
                            onClick={() => upd("pledged_amount", String(n))}
                            className={`px-3 py-2 text-xs font-mono border transition-colors ${
                              String(n) === String(form.pledged_amount)
                                ? "bg-[var(--terracotta)] border-[var(--terracotta)] text-[var(--bone)]"
                                : "border-[var(--bone)]/30 hover:border-[var(--bone)] text-[var(--bone)]/85"
                            }`}
                          >
                            {SYMBOL[currency]}{n.toLocaleString()}
                          </button>
                        ))}
                      </div>
                      <div className="text-xs text-[var(--bone)]/55 mt-3">
                        Your amount is private — no one else will see it. Nothing is charged yet.
                      </div>
                    </div>

                    <label className="block mt-10">
                      <span className="overline text-[var(--bone)]/60">Why are you joining? (optional)</span>
                      <textarea data-testid="member-motivation" rows={3} className="editorial-input on-dark mt-2 resize-none" value={form.motivation} onChange={(e) => upd("motivation", e.target.value)} placeholder="Share what drew you here…" />
                    </label>

                    <button data-testid="member-submit" type="submit" disabled={submitting} className="btn-primary mt-10 disabled:opacity-60">
                      {submitting ? "Welcoming you…" : "Join the Irene community"} <ArrowRight className="w-4 h-4" />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
